import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import {
  useChangeStatusV2,
  useFetchRecordV2,
  useGetAuth,
  useGetPermissionsEachPage,
  useFetchOneAppByTypeV2,
} from "@lhd/hooks";
import { IPermissionItem, Worker } from "@lhd/types";
import { getCompanyId } from "@lhd/utility";
import { useState } from "react";
import { useParams } from "react-router";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ROUTES } from "@lhd/constants";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { BusinessTripReportRecord } from "../BusinessTripReportsList/hook";

const MODAL_CONTENT: Record<string, any> = {
  drafting: {
    owner:
      "この申請を取り下げますか?\n取り下げると、承認者側の一覧から削除され、\nステータスは「新規作成中」に戻ります。 ",
    approval:
      "修正依頼にします。ステータスは「新規作成中」に戻り、\n承認者画面から非表示になります。実行してよろしいですか。",
  },
  submitted:
    "本報告書を管理者へ申請します。\n申請後は内容を編集できなくなりますが、\nよろしいでしょうか。",
  reviewing: "確認中に変更します。\nよろしいでしょうか。",
  approved:
    "この内容を給与明細シートへ反映します。\n反映後は承認を取り下げできなくなりますが、\nよろしいでしょうか。",
  rejected: "却下します。\nよろしいでしょうか。",
};

export const BUSINESS_TRIP_REPORTS_STATUS = {
  DRAFTING: "drafting",
  SUBMITTED: "submitted",
  REVIEWING: "reviewing",
  APPROVED: "approved",
  REJECTED: "rejected",
};

export const findIsApplicant = (
  recordDataRaw: BusinessTripReportRecord,
  currentWorker: Worker,
  _?: AppPartType[],
) => {
  const applicantWorkerIds: number[] =
    recordDataRaw?.business_trip_expense_applicant || [];

  const isApplicant =
    !!currentWorker?.id &&
    applicantWorkerIds?.includes(Number(currentWorker.id));
  return isApplicant;
};

const useEdit = () => {
  const { appId = "", id = "" } = useParams();
  const navigate = useNavigate();
  const { getCurrentWorker, auth, isAdmin, isSuperAdmin } = useGetAuth();
  const currentWorker = getCurrentWorker();
  const userId = auth?.id;

  const appType = APP_TYPES.BUSINESS_TRIP_REPORTS;
  const [openModalChangeStatus, setOpenModalChangeStatus] = useState(false);
  const [targetStatus, setTargetStatus] = useState<string | null>(null);
  const [modalContent, setModalContent] = useState<string>("");

  const permissionsEachPage = useGetPermissionsEachPage();
  const companyId = getCompanyId();

  const { data: recordDataRaw } = useFetchRecordV2({
    appType,
    appId,
    recordId: id,
    companyId,
  });

  const [searchParams] = useSearchParams();
  const pageMode = searchParams.get("pageMode");
  const isEditMode = pageMode === "edit";
  const viewType = searchParams.get("view_type");
  const isOwner = viewType === "owner";

  const { mutateAsync: changeStatusAsync, isLoading: isLoadingChangeStatus } =
    useChangeStatusV2({
      appType,
      appId,
      companyId,
      recordId: id,
    });

  const openConfirmModal = (
    status: string,
    viewType?: "owner" | "approval",
  ) => {
    setModalContent(
      (viewType
        ? MODAL_CONTENT[status]?.[viewType as any]
        : MODAL_CONTENT[status]) ||
        "ステータスを変更します。\nよろしいでしょうか。",
    );
    setTargetStatus(status);
    setOpenModalChangeStatus(true);
  };

  const onOkModalChangeStatus = async () => {
    if (!targetStatus) return;
    try {
      await changeStatusAsync({
        status: targetStatus,
      });
      setOpenModalChangeStatus(false);
      if (!isOwner && targetStatus === BUSINESS_TRIP_REPORTS_STATUS.DRAFTING) {
        navigate(`/${ROUTES.BUSINESS_TRIP_REPORTS}?view_type=${viewType}`);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const permissionItem = permissionsEachPage[
    ROUTES.BUSINESS_TRIP_REPORTS
  ] as IPermissionItem;

  const { data: dataAppRaw } = useFetchOneAppByTypeV2(appType, companyId);
  const appParts = dataAppRaw?.app_parts || [];

  const isApplicant = findIsApplicant(
    recordDataRaw?.data as any,
    currentWorker as Worker,
    appParts,
  );

  const availableStatuses =
    (recordDataRaw?.data as any)?.business_trip_report_info
      ?.available_statuses || [];
  const currentStatus = (recordDataRaw?.data as any)?.business_trip_report_info
    ?.status;
  const createdUserId = (recordDataRaw?.data as any)?.user_id;

  const isCurrentUserCreated = createdUserId === userId;

  const disabledInEditMode =
    !isOwner ||
    currentStatus !== BUSINESS_TRIP_REPORTS_STATUS.DRAFTING ||
    !(isCurrentUserCreated || isApplicant);

  return {
    appType,
    openModalChangeStatus,
    setOpenModalChangeStatus,
    modalContent,
    targetStatus,
    permission: {
      create: true,
      view: true,
      edit: true,
      delete: true,
      all: true,
      ...(permissionItem || {}),
    },
    onOkModalChangeStatus,
    isLoadingChangeStatus,
    disabledInEditMode,
    configShowBtnsOnHeader: {
      btnPrintPdf: true,
      btnSave:
        isOwner &&
        currentStatus === BUSINESS_TRIP_REPORTS_STATUS.DRAFTING &&
        !disabledInEditMode,
      btnPreview: isOwner,
      btnDuplicate: isOwner,
      btnToCreate: isOwner,
      saveAsNew: isOwner,
    },
    permissionItem,
    availableStatuses,
    isEditMode,
    openConfirmModal,
    currentStatus,
    viewType,
    isOwner,
    dataAppRaw,
    isCurrentUserCreated,
    isApplicant,
    isAdmin,
    isSuperAdmin,
    recordDataRaw,
  };
};

export type Props = ReturnType<typeof useEdit>;

export default useEdit;
